import { createAction } from '@reduxjs/toolkit'
import { call, debounce, put, select } from 'redux-saga/effects'

import {
    calculateHorizontalPosition,
    calculateVerticalPosition,
    FlagBounds,
} from '../utils/getFlagDetailsPopupPosition'
import managers from '../../lib/managers'
import { Workspace } from '../../lib/toolBoxes/2D'
import { selectFlagDetails, updateFlagDetails } from '../../slices/materialFlags'

export function computeFlagBoundsFromItem(item: paper.Item): {
    flagBounds: FlagBounds
    projectView: paper.View
} | null {
    const {
        bounds,
        project: { view: projectView },
    } = item

    if (!bounds?.center || !projectView) return null

    const topLeftView = projectView.projectToView(bounds.topLeft)
    const bottomRightView = projectView.projectToView(bounds.bottomRight)
    const canvasRect = (projectView.element as HTMLCanvasElement).getBoundingClientRect()

    const flagBounds = {
        left: topLeftView.x + canvasRect.left,
        top: topLeftView.y + canvasRect.top,
        right: bottomRightView.x + canvasRect.left,
        bottom: bottomRightView.y + canvasRect.top,
    }

    return { flagBounds, projectView }
}

export function* getFlagScreenBounds(workspaceTool: Workspace, predicate: (arg) => boolean) {
    const items: paper.Item[] | null = yield call(workspaceTool.getItemsWithCriteria, 'id', predicate)

    if (!items || items.length === 0) return null

    const item = items[0]
    const { data } = item

    const result = computeFlagBoundsFromItem(item)

    if (!result) return null

    return {
        id: item.id,
        paperId: item.id,
        openingLinkId: data?.id,
        flagId: data?.flag_id,
        projectView: result.projectView,
        flagBounds: result.flagBounds,
    }
}

export function computePopupPosition(flagBounds: FlagBounds, canvas: HTMLCanvasElement, dialogHeight?: number) {
    const IMUPBounds = canvas.getBoundingClientRect()

    const top = calculateVerticalPosition(flagBounds, IMUPBounds, dialogHeight)
    const left = calculateHorizontalPosition(flagBounds, IMUPBounds)

    return { top, left }
}

export const setFlagDetailsPopupHeight = createAction<{ dialogHeight: number }>('setFlagDetailsPopupHeight')

export function* adjustFlagDetailsPopupPositionBasedOnPopupHeight(
    action: ReturnType<typeof setFlagDetailsPopupHeight>
) {
    const paperManager = yield call(managers.get2DManager)

    if (!paperManager) return

    const { dialogHeight } = action.payload

    const workspaceTool: Workspace = yield call(paperManager.getTool, Workspace.NAME)
    const flagDetails = yield select(selectFlagDetails)

    const result = yield call(getFlagScreenBounds, workspaceTool, (id: number) => id === flagDetails?.paperId)

    if (!result) return

    const { paperId, flagId, openingLinkId, projectView, flagBounds } = result

    if (!openingLinkId) return

    const { top, left } = computePopupPosition(flagBounds, projectView.element as HTMLCanvasElement, dialogHeight)

    yield put(
        updateFlagDetails({
            flagId,
            openingLinkId,
            position: { x: left, y: top },
            dialogHeight: dialogHeight || flagDetails.dialogHeight,
            paperId,
        })
    )
}

export function* watchForSetFlagDetailsPopupHeight() {
    yield debounce(10, setFlagDetailsPopupHeight.type, adjustFlagDetailsPopupPositionBasedOnPopupHeight)
}
